import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { DatePickerModule } from 'primeng/datepicker';
import { Select } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { DateFormatterIso } from '../../../shared/utilities/date-formatter.utils';
import { TimeFormatterIso } from '../../../shared/utilities/date-formatter.utils';
import { getNameInitial } from '../../../shared/utilities/get-name-initial.utils';
import { getProfileImageUrl } from '../../../shared/utilities/users.utils';
import { CommonService } from '../../../shared/services/common/common.service';
import { AuditLogService } from '../../../shared/services/audit-log/audit-log.service';
import { PMSRouteEnum } from '../../../shared/enums/route.enum';
import type {
  AuditLogDetailsGroup,
  AuditLogDetailItem,
  AuditLogDetailsQueryParams,
  AuditLogDetailsResult,
} from '../../../shared/interfaces/audit-log/audit-log.interface';
import {
  setSearchParams,
  toQueryParams,
} from '../../../shared/utilities/common.utils';

const ACTION_OPTIONS = [
  { label: 'All Actions', value: '' },
  { value: 'CREATE', label: 'Create' },
  { value: 'UPDATE', label: 'Update' },
  { value: 'DELETE', label: 'Delete' },
];

@Component({
  selector: 'app-audit-log-details',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    MatIconModule,
    DatePickerModule,
    Select,
    TooltipModule,
  ],
  templateUrl: './audit-log-details.component.html',
  styleUrl: './audit-log-details.component.scss',
})
export class AuditLogDetailsComponent implements OnInit {
  readonly DateFormatterIso = DateFormatterIso;
  readonly TimeFormatterIso = TimeFormatterIso;
  readonly PMSRouteEnum = PMSRouteEnum;
  readonly actionOptions = ACTION_OPTIONS;
  readonly getNameInitial = getNameInitial;
  readonly getProfileImageUrl = getProfileImageUrl;

  groups: AuditLogDetailsGroup[] = [];
  total = 0;
  page = 1;
  limit = 10;
  totalPages = 0;
  loading = false;

  readonly rowsPerPageOptions = [
    { label: '10', value: 10 },
    { label: '20', value: 20 },
    { label: '50', value: 50 },
    { label: '100', value: 100 },
  ];

  entity = '';
  entityId = '';
  filterAction = '';
  dateFrom: Date | null = null;
  dateTo: Date | null = null;

  expandedLogId: string | null = null;

  constructor(
    public readonly commonService: CommonService,
    private readonly auditLogService: AuditLogService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe((qp) => {
      this.entity = qp['entity'] ?? '';
      this.entityId = qp['entityId'] ?? '';
      this.filterAction = qp['action'] ?? '';
      this.page = parseInt(qp['page'] ?? String(this.page), 10);
      this.limit = parseInt(qp['limit'] ?? String(this.limit), 10);
      if (qp['dateFrom']) this.dateFrom = new Date(qp['dateFrom']);
      else this.dateFrom = null;
      if (qp['dateTo']) this.dateTo = new Date(qp['dateTo']);
      else this.dateTo = null;

      setSearchParams(qp);

      if (this.entity && this.entityId) {
        this.loadDetails();
      }
    });
  }

  loadDetails(): void {
    this.loading = true;
    const params: AuditLogDetailsQueryParams = {
      entity: this.entity,
      entityId: this.entityId,
      page: this.page,
      limit: this.limit,
    };
    if (this.filterAction) params.action = this.filterAction;
    if (this.dateFrom)
      params.dateFrom = this.dateFrom.toISOString().split('T')[0];
    if (this.dateTo) params.dateTo = this.dateTo.toISOString().split('T')[0];

    this.auditLogService.getLogDetails(params).subscribe({
      next: (res: AuditLogDetailsResult) => {
        this.groups = res?.data ?? [];
        this.total = res?.total ?? 0;
        this.page = res?.page ?? 1;
        this.limit = res?.limit ?? 50;
        this.totalPages = res?.totalPages ?? 0;
        this.loading = false;
      },
      error: () => {
        this.groups = [];
        this.total = 0;
        this.loading = false;
      },
    });
  }

  goToPage(pageNum: number): void {
    if (pageNum < 1 || pageNum > this.totalPages) return;
    this.page = pageNum;
    this.router.navigate([], {
      queryParams: toQueryParams(this.getQueryParams()),
      queryParamsHandling: '',
    });
  }

  onLimitChange(newLimit: number): void {
    this.limit = newLimit;
    this.page = 1;
    this.router.navigate([], {
      queryParams: toQueryParams(this.getQueryParams()),
      queryParamsHandling: '',
    });
  }

  private getQueryParams(): Record<string, unknown> {
    const qp: Record<string, unknown> = {
      entity: this.entity,
      entityId: this.entityId,
      page: this.page,
      limit: this.limit,
    };
    if (this.filterAction) qp['action'] = this.filterAction;
    if (this.dateFrom) qp['dateFrom'] = this.dateFrom;
    if (this.dateTo) qp['dateTo'] = this.dateTo;
    return qp;
  }

  get pageReport(): string {
    if (this.total === 0) return 'No entries';
    const from = (this.page - 1) * this.limit + 1;
    const to = Math.min(this.page * this.limit, this.total);
    return `Showing ${from} to ${to} of ${this.total} entries`;
  }

  /** Trigger load when any single filter changes (replaces Apply button). */
  onFilterChange(): void {
    this.page = 1;
    this.router.navigate([], {
      queryParams: toQueryParams(this.getQueryParams()),
      queryParamsHandling: '',
    });
  }

  clearFilters(): void {
    this.filterAction = '';
    this.dateFrom = null;
    this.dateTo = null;
    this.page = 1;
    this.router.navigate([], {
      queryParams: toQueryParams(this.getQueryParams()),
      queryParamsHandling: '',
    });
  }

  getActorDisplay(log: AuditLogDetailItem): string {
    const a = log?.actor;
    if (!a) return '—';
    const first = a.firstName ?? '';
    const last = a.lastName ?? '';
    if (first || last) return `${first} ${last}`.trim();
    return a.email ?? a.userId ?? '—';
  }

  /** Fallback when actor avatar image fails to load: hide img, show initial circle. */
  onAvatarError(event: Event): void {
    const img = event.target as HTMLImageElement;
    if (img) img.style.display = 'none';
    const next = img?.nextElementSibling as HTMLElement;
    if (next) next.style.display = 'inline-flex';
  }

  toggleExpand(logId: string): void {
    this.expandedLogId = this.expandedLogId === logId ? null : logId;
  }

  isExpanded(logId: string): boolean {
    return this.expandedLogId === logId;
  }

  goBack(): void {
    const pathSegments = PMSRouteEnum.PMS_AUDIT_LOG.split('/').filter(Boolean);
    this.router.navigate([PMSRouteEnum.PMS_BASE_URL, ...pathSegments]);
  }
}
